import { Modal, Button, Form, Col, Row, Container } from 'react-bootstrap';
import VirtualizedTable from "../Grids/VirtualizedTable.js";
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from "react-redux";
import '../../assets/Styles/MultiTripCancellationCSS/MultiTripCancellationCSS.css';
import APICom from '../../utils/helpers/APICom';

const MultiTripCancellation = ({ GridRowData, selectedDate, setShowMultiTripCancellationDialog, showMultiTripCancellationDialog }) => {
    const [multiCancelAlert, setMultiCancelAlert] = useState({ showAlert: false, alertTitle: '', alertMsg: '', closeCancelDialog:false });
    const [rowData, setRowData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [initialSelectedRows, setInitialSelectedRows] = useState({});
    const [cancelReasons, setCancelReasons] = useState([]);
    const [CancelReasonID, setCancelReasonID] = useState(-1);
    const [comment, setComment] = useState("");
    
    const tableRef = useRef(null);
    const isMounted = useRef(true);
    const fetchTracker = useRef("");
    const hiddenColumns = useRef(['VehicleID','Queue', 'SchDetailID', 'ClientID', 'ServiceID']).current;

    const { CancelReasonData } = useSelector((store) => store.combinedata);
    const { loginData }  = useSelector((store) => store.login);

    // --- LOGIC: STABILIZING DEPENDENCIES ---
    const selectionKey = useMemo(() => {
        return GridRowData?.map(item => item.iRefID).join(",") || "";
    }, [GridRowData]);

    // --- LOGIC: API DATA FETCHING ---
    const GetMultiTripDataForCancellation = useCallback(async () => {
        if (!selectionKey) return;

        try {
            const ClientIDList = GridRowData.map(item => item.ClientID).join(",");
            const apiRequest = {
                ClientIDs: ClientIDList,
                PickupDate: String(selectedDate),
                AffiliateID: GridRowData[0].iSRATSPID
            };

            const response = await APICom.fetchData(
                "ReservationCoreURL", 
                "api/ParatransitCT/GetMultiTripDataForCancellation", 
                "post", 
                { "Content-Type": "application/json" }, 
                apiRequest, 
                null
            );

            // Logic Check: Component unmounted during await?
            if (!isMounted.current) return;

            if (response && !response.error && response.data) {
                const data = response.data;
                const selectedTripIds = selectionKey.split(",");
                
                const enhancedData = data.map((row, index) => ({
                    ...row,
                    TripNo: row.TripNo || row.tripNo || row.TRIPNO || `row-${index}`
                }));

                // Logic Check: Guard against state updates on unmounted component
                setRowData(enhancedData);

                const preSelectedRows = {};
                enhancedData.forEach((row) => {
                    const rowConfirmNum = String(row["Confirm#"] || row["confirm#"] || "");
                    if (selectedTripIds.includes(rowConfirmNum)) {
                        preSelectedRows[row.TripNo] = true;
                    }
                });

                if (Object.keys(preSelectedRows).length > 0) {
                    setInitialSelectedRows(preSelectedRows);
                }

                if (data.length > 0) {
                    generateColumns(data[0]);
                }
            } else {
                setMultiCancelAlert({ 
                    showAlert: true, 
                    alertTitle: 'Error', 
                    alertMsg: response?.error || "Failed to fetch trip data.",
                    closeCancelDialog: true 
                });
            }
        } catch (err) {
            console.error("Fetch Error:", err);
        }
    }, [selectionKey, selectedDate]);

    // --- LOGIC: DYNAMIC COLUMN GENERATION ---
    const generateColumns = (sampleData) => {
        const cols = Object.keys(sampleData).map((key) => {
            const lowerKey = key.toLowerCase();
            const config = {
                accessorKey: key,
                header: key.charAt(0).toUpperCase() + key.slice(1),
                size: 150 // Default size
            };

            if (lowerKey === 'return') {
                config.size = 75;
                config.cell = ({ getValue }) => (
                    <div className="d-flex justify-content-start ps-2">
                        <input type="checkbox" checked={!!getValue()} readOnly />
                    </div>
                );
            } else if (lowerKey === 'confirm#') {
                config.size = 100;
                config.cell = ({ getValue }) => ( 
                    <div style={{ textAlign: "left" }}> 
                    {getValue()} 
                    </div>
                );
            } else if (lowerKey === 'pickupperson') {
                config.size = 160;
                config.cell = ({ getValue }) => ( 
                    <div style={{ textAlign: "left" }}> 
                    {getValue()} 
                    </div>
                );
            } else if (lowerKey === 'tripstatus') {
                config.size = 120;
                config.cell = ({ getValue }) => ( 
                    <div style={{ textAlign: "left" }}> 
                    {getValue()} 
                    </div>
                );
            } else if (lowerKey === 'appttime') {
                config.size = 100;
                config.cell = ({ getValue }) => ( 
                    <div style={{ textAlign: "left" }}> 
                    {getValue()} 
                    </div>
                );
            }  
            else if (lowerKey === 'picktime') {
                config.size = 95;
                config.cell = ({ getValue }) => ( 
                    <div style={{ textAlign: "left" }}> 
                    {getValue()} 
                    </div>
                );
            } 
            else if (lowerKey === 'pickaddress' || lowerKey === 'dropaddress') {
                config.size = 325;
                config.cell = ({ getValue }) => ( 
                    <div style={{ textAlign: "left" }}> 
                    {getValue()} 
                    </div>
                );
            }

            return config;
        });
        setColumns(cols);
    };

    // --- LIFECYCLE ---
    useEffect(() => {
        isMounted.current = true;
        const currentFetchInstance = `${selectionKey}_${selectedDate}`;

        if (!selectionKey) {
            setMultiCancelAlert({ 
                showAlert: true, 
                alertTitle: 'No trip selected', 
                alertMsg: 'Please select a trip to cancel.',
                closeCancelDialog: true 
            });
        } else if (fetchTracker.current !== currentFetchInstance) {
            fetchTracker.current = currentFetchInstance;
            GetMultiTripDataForCancellation();
        }
        return () => { isMounted.current = false; };
    }, [GetMultiTripDataForCancellation, selectionKey, selectedDate]);

    // --- HANDLERS ---
    const handleCancelReasonChange = (e) => {
        const selectedId = Number(e.target.value);
        setCancelReasonID(selectedId);
        const reason = cancelReasons.find(item => item.id === selectedId);
        if (reason) {
            setComment(reason.description || "");
        }
    };

    const LoadingCancelReasonByAffiliate = useCallback(() => {
        if (!GridRowData || GridRowData.length === 0) return;

        try {
            const tspId = GridRowData[0].iSRATSPID;
            const rawData = CancelReasonData?.[parseInt(tspId)];
            
            if (rawData) {
                const cancelReasonJSON = JSON.parse(rawData);
                if (Array.isArray(cancelReasonJSON)) {
                    const formatted = cancelReasonJSON.map(item => ({
                        name: item.vTitle,
                        id: item.iId,
                        description: item.vDescription
                    }));
                    setCancelReasons(formatted);
                    // Set default comment if reasons exist
                    if (formatted.length > 0)
                    { 
                        setCancelReasonID(formatted[0].id);
                        setComment(formatted[0].description);
                    }
                }
            }
        } catch (err) {
            console.error("Cancel Reason Parse Error:", err);
        }
    }, [CancelReasonData, GridRowData]);

    const handleAlertModalClose = () => {
        setMultiCancelAlert(prev => ({ ...prev, showAlert: false }));
        if (multiCancelAlert.closeCancelDialog === true) {
            setShowMultiTripCancellationDialog(false);
        }
    };

     const handlePerformCancelAction = async (e) => {
        const GridRowData = tableRef.current?.getSelectedRows();
        console.log("Submitting:", { GridRowData, CancelReasonID, comment });
        let cancelTripCount = GridRowData.length;
        if(cancelTripCount > 0)
        {
           let cancelRequestList = [];

            for (let i = 0; i < cancelTripCount; i++) {
                cancelRequestList.push({
                    ServiceID: GridRowData[i]["ServiceID"],
                    CancelReasonID: CancelReasonID,
                    CancelReason: comment,
                    UserID: loginData.iUserID,
                    AppID: 26,
                    Return: GridRowData[i]["Return"],
                    VehicleID: GridRowData[i]["VehicleID"],
                    SchDetailID: GridRowData[i]["SchDetailID"],
                    Queue: GridRowData[i]["Queue"],
                    ConfirmNo: GridRowData[i]["Confirm#"]
                });
            }
            console.log(JSON.stringify(cancelRequestList));
            if (cancelRequestList.length > 0) {
                const response = await APICom.fetchData(
                    "ReservationCoreURL",
                    "api/ParatransitCT/PerformMultiTripCancellation",
                    "post",
                    { "Content-Type": "application/json" },
                    JSON.stringify(cancelRequestList),
                    null
                );
                if(response && response.error === "")
                {
                     const formattedMsg = (
                        <>
                            <ul className="list-unstyled mb-0">
                                {response.data.map((item, index) => (
                                    <li key={index} className="mb-2">
                                        <b>Confirmation # </b>{item.ConfirmNo} - {item.Status}
                                    </li>
                                ))}
                            </ul>
                        </>
                    );
                    setMultiCancelAlert({ 
                        showAlert: true, 
                        alertTitle: 'Cancellation Result(s)', 
                        alertMsg: formattedMsg,
                        closeCancelDialog: true
                    });
                }
                else
                {
                    setMultiCancelAlert({ 
                        showAlert: true, 
                        alertTitle: 'Cancellation Failed', 
                        alertMsg: response.error 
                    });
                }
            }
        }
        else
        {
            setMultiCancelAlert({ 
                showAlert: true, 
                alertTitle: 'No trip selected', 
                alertMsg: 'Please select a trip to cancel.',
                closeCancelDialog: false 
            });
        }
    };

    return (
        <>
            <Modal 
                dialogClassName="custom-modal-dimensions" 
                show={showMultiTripCancellationDialog} 
                onHide={() => setShowMultiTripCancellationDialog(false)} 
                backdrop="static"
                onShow={LoadingCancelReasonByAffiliate}
            >
                <Modal.Header closeButton className="custom-header-style">
                    <Modal.Title>Cancel Trip</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Container fluid>
                        <Row className="mb-3">
                            <Col style={{ minHeight: "210px" }}>
                                <VirtualizedTable 
                                    ref={tableRef}
                                    data={rowData} 
                                    columns={columns} 
                                    height={"28vh"} 
                                    showCheckbox={true}
                                    initialSelectedRows={initialSelectedRows}
                                    hiddenColumns={hiddenColumns}
                                    showRecordCount={false}
                                    paddingBackgroundTheme={30}
                                />
                            </Col>
                        </Row>
                        <Form>
                            <Form.Group className="mb-2">
                                <Form.Label><b>Reason</b></Form.Label>
                                <Form.Select onChange={handleCancelReasonChange}>
                                    {cancelReasons.map(({ id, name }) => (
                                        <option key={id} value={id}>{name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-0">
                                <Form.Label><b>Any Comments</b></Form.Label>
                                <Form.Control 
                                    as="textarea" 
                                    rows={2} 
                                    value={comment} 
                                    onChange={(e) => setComment(e.target.value)} 
                                />
                            </Form.Group>
                        </Form>
                    </Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button className="custom-ok-button-style" onClick={handlePerformCancelAction}>Perform Cancel</Button>
                    <Button variant="secondary" onClick={() => setShowMultiTripCancellationDialog(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Alert Modal */}
            <Modal show={multiCancelAlert.showAlert} onHide={handleAlertModalClose} centered backdrop="static">
                <Modal.Header closeButton className='custom-header-style'>
                    <Modal.Title>{multiCancelAlert.alertTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body><b>{multiCancelAlert.alertMsg}</b></Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleAlertModalClose}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default MultiTripCancellation;